/**
 * Tienda e inventario. Se compra sólo en la base propia (cerca del nexo) o
 * estando muerto; vender devuelve SHOP.sellRatio del precio. Hasta 6 objetos.
 */
export const SHOP = {
  /** distancia al nexo propio desde la que se puede comprar */
  radius: 11,
  sellRatio: 0.7,
  slots: 6,
};

/** Estadísticas que suma un objeto (mismas claves que HeroStats). */
export interface ItemStats {
  maxHp?: number;
  hpRegen?: number;
  attackDamage?: number;
  /** suma al multiplicador de velocidad de ataque (0.15 = +15%) */
  attackSpeed?: number;
  armor?: number;
  abilityHaste?: number;
  moveSpeed?: number;
  omnivamp?: number;
  lethality?: number;
  armorPen?: number;
  tenacity?: number;
}

export interface ItemDef {
  id: string;
  name: string;
  /** abreviatura para el icono del inventario */
  short: string;
  /** color CSS del icono */
  color: string;
  cost: number;
  stats: ItemStats;
  /** objeto completo (más caro, combina estadísticas) */
  complete?: boolean;
}

const list: ItemDef[] = [
  { id: 'short-sword', name: 'Espada corta', short: 'EC', color: '#ff9f6b', cost: 350, stats: { attackDamage: 10 } },
  { id: 'leather-armor', name: 'Armadura de cuero', short: 'AC', color: '#8fb8ff', cost: 300, stats: { armor: 15 } },
  { id: 'life-crystal', name: 'Cristal de vida', short: 'CV', color: '#7ce6a0', cost: 400, stats: { maxHp: 150 } },
  { id: 'swift-boots', name: 'Botas veloces', short: 'BV', color: '#d6c27a', cost: 300, stats: { moveSpeed: 1.0 } },
  { id: 'agile-gloves', name: 'Guantes ágiles', short: 'GA', color: '#ffd166', cost: 350, stats: { attackSpeed: 0.15 } },
  { id: 'time-amulet', name: 'Amuleto del tiempo', short: 'AT', color: '#c9a2ff', cost: 400, stats: { abilityHaste: 15 } },
  { id: 'regen-ring', name: 'Anillo de regeneración', short: 'AR', color: '#9ff0c8', cost: 300, stats: { hpRegen: 2.5 } },
  { id: 'vampire-fang', name: 'Colmillo vampírico', short: 'CO', color: '#ff6b8a', cost: 450, stats: { omnivamp: 0.08, attackDamage: 5 } },
  { id: 'shadow-dagger', name: 'Daga sombría', short: 'DS', color: '#9a8cff', cost: 500, stats: { lethality: 10, attackDamage: 8 } },
  { id: 'helm-breaker', name: 'Martillo rompeyelmos', short: 'MR', color: '#ffb36b', cost: 650, stats: { armorPen: 0.2, attackDamage: 12 }, complete: true },
  { id: 'stone-heart', name: 'Corazón de piedra', short: 'CP', color: '#9fd4ff', cost: 1000, stats: { maxHp: 300, armor: 20, tenacity: 0.15 }, complete: true },
  { id: 'koopa-shell', name: 'Caparazón de Koopa', short: 'CK', color: '#5fbf4a', cost: 900, stats: { armor: 40, maxHp: 120 }, complete: true },
  { id: 'hero-blade', name: 'Hoja del héroe', short: 'HH', color: '#ffe08a', cost: 1100, stats: { attackDamage: 25, abilityHaste: 10, attackSpeed: 0.1 }, complete: true },
];

export const ITEMS: Record<string, ItemDef> = Object.fromEntries(list.map((item) => [item.id, item]));
export const ITEM_ORDER: string[] = list.map((item) => item.id);

export function getItem(id: string): ItemDef | undefined {
  return ITEMS[id];
}

/** Suma de las estadísticas de un inventario. */
export function itemBonuses(items: readonly string[]): Required<ItemStats> {
  const total: Required<ItemStats> = {
    maxHp: 0, hpRegen: 0, attackDamage: 0, attackSpeed: 0, armor: 0, abilityHaste: 0,
    moveSpeed: 0, omnivamp: 0, lethality: 0, armorPen: 0, tenacity: 0,
  };
  for (const id of items) {
    const stats = ITEMS[id]?.stats;
    if (!stats) continue;
    for (const key of Object.keys(stats) as Array<keyof ItemStats>) total[key] += stats[key] ?? 0;
  }
  return total;
}

export function sellPrice(id: string): number {
  // redondear, no truncar: 350 × 0.7 da 244.999… en coma flotante
  return Math.round((ITEMS[id]?.cost ?? 0) * SHOP.sellRatio);
}

/** Orden en que compran los bots según el rol de su héroe. */
export const BOT_BUILDS: Record<string, string[]> = {
  fighter: ['short-sword', 'leather-armor', 'swift-boots', 'hero-blade', 'stone-heart', 'helm-breaker'],
  tank: ['life-crystal', 'leather-armor', 'swift-boots', 'koopa-shell', 'stone-heart', 'regen-ring'],
  marksman: ['agile-gloves', 'short-sword', 'swift-boots', 'hero-blade', 'helm-breaker', 'vampire-fang'],
  assassin: ['shadow-dagger', 'short-sword', 'swift-boots', 'helm-breaker', 'hero-blade', 'vampire-fang'],
};
